import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import CloseIcon from '@/assets/Icons/Close.svg?react';
import styles from './Flyout.module.scss';

interface FlyoutContextProps {
	closeFlyout: () => void;
}

interface FlyoutCloseButtonProps {
	className?: string;
}

interface FlyoutProps {
	id: string;
	button: ReactNode;
	onOpen?: () => void;
	onToggle?: () => void;
	className?: string;
	children?: ReactNode;
	flyoutWrapperClassName?: string;
	onClose?: () => void;
}

const FlyoutContext = createContext<FlyoutContextProps | null>(null);

const useFlyout = () => {
	const context = useContext(FlyoutContext);
	if (!context) {
		throw new Error('useFlyout must be used within a Flyout');
	}
	return context;
};

export const FlyoutCloseButton: React.FC<FlyoutCloseButtonProps> = ({ className }) => {
	const { closeFlyout } = useFlyout();
	return (
		<button className={classNames(styles.closeFlyoutBttn, className)} onClick={closeFlyout} aria-label="Close">
			<CloseIcon />
		</button>
	);
};

const Flyout: React.FC<FlyoutProps> = ({
	id,
	button,
	onOpen,
	onToggle,
	className,
	children,
	flyoutWrapperClassName,
	onClose,
}) => {
	const [openFlyoutId, setOpenFlyoutId] = useState<string | null>(null);
	const [visible, setVisible] = useState(false);
	const sideMenuRef = useRef<HTMLDivElement>(null);

	const isFlyoutOpen = openFlyoutId === id;

	const openFlyout = useCallback(() => {
		setOpenFlyoutId(id);
		onOpen?.();
		// Push a new history state when opening the flyout
		window.history.pushState({ flyout: id }, '', window.location.href);
		// Wait one frame before showing to ensure initial state is rendered
		requestAnimationFrame(() => {
			setVisible(true);
		});
	}, [id, onOpen]);

	const closeFlyout = useCallback(() => {
		// Start closing animation
		setVisible(false);
		onClose && onClose();
	}, [onClose]);

	// If we rely on CSS transitions, listen for the end of the close transition
	useEffect(() => {
		const sideMenuEl = sideMenuRef.current;
		if (!sideMenuEl) return;

		const handleTransitionEnd = (e: TransitionEvent) => {
			if (e.target === sideMenuEl && !visible) {
				// Only reset the ID after the close animation completes
				setOpenFlyoutId(null);
			}
		};

		sideMenuEl.addEventListener('transitionend', handleTransitionEnd);
		return () => {
			sideMenuEl.removeEventListener('transitionend', handleTransitionEnd);
		};
	}, [visible]);

	// Handle back button (popstate) event
	useEffect(() => {
		if (!isFlyoutOpen) return;

		const handlePopState = (event: PopStateEvent) => {
			event.preventDefault(); // Prevent default back navigation
			closeFlyout(); // Close the flyout
		};

		window.addEventListener('popstate', handlePopState);
		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [isFlyoutOpen, closeFlyout]);

	const toggleFlyout = () => {
		if (isFlyoutOpen && visible) {
			closeFlyout();
			// Go back in history to remove the flyout state
			window.history.back();
		} else {
			openFlyout();
		}
		onToggle && onToggle();
	};

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape' && (isFlyoutOpen || visible)) {
				closeFlyout();
				window.history.back(); // Ensure history is updated on Escape
			}
		},
		[closeFlyout, isFlyoutOpen, visible],
	);

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [handleKeyDown]);

	return (
		<div className={flyoutWrapperClassName}>
			<span onClick={toggleFlyout}>{button}</span>
			{openFlyoutId === id && (
				<FlyoutContext.Provider value={{ closeFlyout }}>
					{ReactDOM.createPortal(
						<div
							ref={sideMenuRef}
							className={classNames(styles.sideMenu, {
								[styles.visibleSideMenu]: visible,
							})}
						>
							<div className={styles.overlay} onClick={closeFlyout}></div>
							<div className={classNames(styles.flyoutContent, className)}>{children}</div>
						</div>,
						document.body,
					)}
				</FlyoutContext.Provider>
			)}
		</div>
	);
};

export default Flyout;
